import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import {
  PROJECT_STATUS_LABELS,
  ProjectStatusBadge,
} from '@/components/organisms/projects/ProjectStatusBadge';
import { ProjectOverviewTab } from '@/components/organisms/projects/ProjectOverviewTab';
import { ProjectJobsTab } from '@/components/organisms/projects/ProjectJobsTab';
import { ProjectDocumentsTab } from '@/components/organisms/projects/ProjectDocumentsTab';
import { ProjectDependenciesCard } from '@/components/organisms/projects/ProjectDependenciesCard';
import { ProjectFilesTab } from '@/components/organisms/projects/ProjectFilesTab';
import { EditProjectDialog } from '@/components/organisms/projects/EditProjectDialog';
import { ProjectBillingTab } from '@/components/organisms/projects/ProjectBillingTab';
import { ProjectCostsTab } from '@/components/organisms/projects/ProjectCostsTab';
import {
  useProjectEntities,
  useProjectSummary,
  useUpdateProject,
} from '@/hooks/projects/useProjects';
import { fmtMoney } from '@/lib/projects/payAppMath';

const PROJECT_TABS = ['overview', 'jobs', 'documents', 'billing', 'costs', 'files'] as const;
type ProjectTab = (typeof PROJECT_TABS)[number];

export const Route = createFileRoute('/_dashboard/dashboard/projects/$projectId')({
  validateSearch: (search: Record<string, unknown>): { tab: ProjectTab } => ({
    tab: PROJECT_TABS.includes(search['tab'] as ProjectTab)
      ? (search['tab'] as ProjectTab)
      : 'overview',
  }),
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const summaryQuery = useProjectSummary(projectId);
  const entitiesQuery = useProjectEntities(projectId);
  const updateProject = useUpdateProject(projectId);

  const summary = summaryQuery.data;
  const project = summary?.project;
  // Every category the Documents tab renders — change orders and deposits
  // included, or the badge undercounts the rows sitting behind it.
  const documentCount = summary
    ? summary.counts.estimates +
      summary.counts.invoices +
      summary.counts.expenses +
      summary.counts.changeOrders +
      summary.counts.deposits
    : 0;

  if (summaryQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (summaryQuery.isError && summaryQuery.error?.status !== 404) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Could not load this project.</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => void summaryQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!project || !summary) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Project not found.</p>
        <Button asChild variant="link">
          <Link to="/dashboard/projects">Back to projects</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/dashboard/projects">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Projects
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">{project.name}</h1>
              <ProjectStatusBadge status={project.status} />
            </div>
            <p className="text-muted-foreground text-sm">
              {project.projectNumber}
              {summary.financials
                ? ` · contract ${fmtMoney(summary.financials.contractValue)} · outstanding ${fmtMoney(summary.financials.outstanding)}`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EditProjectDialog project={project} projectId={projectId} />
          <Select
            value={project.status}
            onValueChange={(status) => updateProject.mutate({ status })}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: '/dashboard/projects/$projectId',
            params: { projectId },
            search: { tab: value as ProjectTab },
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="jobs">Jobs ({summary.jobs.total})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documentCount})</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <ProjectOverviewTab summary={summary} />
        </TabsContent>
        <TabsContent value="jobs" className="mt-4">
          <div className="space-y-4">
            <ProjectJobsTab projectId={projectId} jobs={entitiesQuery.data?.jobs ?? []} />
            <ProjectDependenciesCard projectId={projectId} jobs={entitiesQuery.data?.jobs ?? []} />
          </div>
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          {entitiesQuery.data ? (
            <ProjectDocumentsTab entities={entitiesQuery.data} projectId={projectId} />
          ) : (
            <p className="text-muted-foreground py-8 text-sm">Loading…</p>
          )}
        </TabsContent>
        <TabsContent value="billing" className="mt-4">
          <ProjectBillingTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="costs" className="mt-4">
          <ProjectCostsTab summary={summary} />
        </TabsContent>
        <TabsContent value="files" className="mt-4">
          <ProjectFilesTab projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
